import { FC, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { defaultConfig } from "../../../hooks/useSearch";
import { IEsimZone } from "../../../interfaces/esim/EsimZone.types";
import { foldSearchText } from "../../../utils/searchText";
import { WithTip } from "../../ui/InfoTip";
import NotfoundTable from "../../ui/NotfoundTable";
import Table, { Row } from "../../ui/table";

const PACKAGES_TIP = "Zona shfaqet në web sapo t'i shtosh paketën e parë.";

const OCS_ID_TIP =
  "Zona krijohet në OCS nga rrjetet e tarifës sonë në shtetet që zgjedh, " +
  "pastaj ruhet këtu me të njëjtën ID.";

const COLUMNS = [
  "Zona",
  "Adresa",
  "Lloji",
  "Shtete",
  <WithTip key="packages" label="Paketa" tip={PACKAGES_TIP} />,
  <WithTip key="id" label="ID (OCS)" tip={OCS_ID_TIP} />,
  ""
];

const CRUMBS = ["Esim", "Zonat"];

interface Props {
  zones: IEsimZone[];
  loading: boolean;
}

// Every zone is listed at once (a couple of hundred), so the page header's search filters here.
const ZonesTable: FC<Props> = ({ zones, loading }) => {
  const [searchParams] = useSearchParams();
  const term = foldSearchText(searchParams.get(defaultConfig.querySelector) ?? "");

  const shown = useMemo(
    () =>
      term
        ? zones.filter(
            (z) =>
              foldSearchText(z.nameSq ?? "").includes(term) ||
              foldSearchText(z.nameEn ?? "").includes(term) ||
              String(z.id) === term
          )
        : zones,
    [zones, term]
  );

  return (
    <>
      <Table titles={COLUMNS} loading={loading} style="hover">
        {shown.map((z) => (
          <Row
            key={z.id}
            name={
              <>
                <span className="font-medium">{z.nameSq ?? "–"}</span>{" "}
                <span className="text-neutral text-xs">{z.nameEn ?? ""}</span>
              </>
            }
            slug={<span className="text-xs">{z.slug ? `esim/${z.slug}` : "–"}</span>}
            kind={z.isRegional ? "Rajonale" : "Shtet"}
            countries={z.countryCount}
            packages={
              z.packageCount > 0 ? (
                z.packageCount
              ) : (
                <span className="text-xs text-red" title="Pa paketa nuk shfaqet në web">
                  0, jo në web
                </span>
              )
            }
            id={<span className="text-xs text-neutral">{z.id}</span>}
            open={
              <span className="flex gap-3 whitespace-nowrap">
                <Link to={`/esim/zones/edit/${z.id}`} state={CRUMBS} className="text-sm underline">
                  Ndrysho
                </Link>
                {z.slug && (
                  <Link
                    to={`/esim/packages?zone=${encodeURIComponent(z.slug)}`}
                    className="text-sm underline"
                  >
                    Paketat
                  </Link>
                )}
              </span>
            }
          />
        ))}
      </Table>
      {!loading && shown.length === 0 && (
        <NotfoundTable header="Nuk ka zona" paragraph="Asnjë zonë nuk përputhet." />
      )}
    </>
  );
};

export default ZonesTable;
